import React, { useState, useEffect } from 'react';
import { useUpload } from '../context/UploadContext';
import { 
  DocumentIcon, 
  ArrowPathIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  XMarkIcon, 
  ClockIcon,
  ChevronUpIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';

const statusConfig = {
  starting: { text: 'Starting...', icon: ArrowPathIcon, color: 'text-gray-400', spin: true },
  uploading: { text: 'Uploading', icon: ArrowPathIcon, color: 'text-blue-500', spin: true },
  pending: { text: 'Queued', icon: ClockIcon, color: 'text-yellow-500', spin: false },
  processing: { text: 'Processing', icon: ArrowPathIcon, color: 'text-purple-500', spin: true },
  complete: { text: 'Complete', icon: CheckCircleIcon, color: 'text-green-500', spin: false },
  failed: { text: 'Failed', icon: XCircleIcon, color: 'text-red-500', spin: false },
  error: { text: 'Error', icon: XCircleIcon, color: 'text-red-500', spin: false },
};

function UploadItem({ upload, compact = false }) {
  const { clearUpload } = useUpload();
  const { id, file, status, progress, error } = upload;
  
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  if (compact) {
    return (
      <div className="flex items-center justify-between py-2 px-3 hover:bg-gray-50 rounded">
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <Icon className={`h-4 w-4 ${config.color} ${config.spin ? 'animate-spin' : ''} flex-shrink-0`} />
          <span className="text-sm text-gray-700 truncate">{file.name}</span>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          {status === 'uploading' && (
            <span className="text-xs text-gray-500">{progress}%</span>
          )}
          {(status === 'complete' || status === 'failed' || status === 'error') && (
            <button onClick={() => clearUpload(id)} className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-3 py-2">
      <Icon className={`h-5 w-5 ${config.color} ${config.spin ? 'animate-spin' : ''}`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
        <p className={`text-xs ${error ? 'text-red-600' : 'text-gray-500'}`}>
          {error || (status === 'uploading' ? `${config.text}... ${progress}%` : config.text)}
        </p>
        {status === 'uploading' && (
          <div className="w-full bg-gray-200 rounded-full h-1 mt-1">
            <div 
              className="bg-blue-600 h-1 rounded-full transition-all duration-300" 
              style={{ width: `${progress}%` }}
            />
          </div>
        )}
      </div>
      <button onClick={() => clearUpload(id)} className="text-gray-400 hover:text-gray-600">
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function UploadIndicator() {
  const { uploads, clearUpload } = useUpload();
  const [expanded, setExpanded] = useState(false);
  
  const allUploads = Object.values(uploads);
  
  // Auto-clear completed uploads after delay
  useEffect(() => {
    const completedUploads = allUploads.filter(u => 
      u.status === 'complete' || u.status === 'failed' || u.status === 'error'
    );
    
    if (completedUploads.length > 0) {
      const timer = setTimeout(() => {
        completedUploads.forEach(u => clearUpload(u.id));
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [allUploads, clearUpload]);

  if (allUploads.length === 0) return null;

  // Calculate summary stats
  const stats = allUploads.reduce((acc, u) => {
    if (u.status === 'complete') acc.complete++;
    else if (u.status === 'failed' || u.status === 'error') acc.failed++;
    else acc.active++;
    return acc;
  }, { active: 0, complete: 0, failed: 0 });

  const total = allUploads.length;
  const isAllDone = stats.active === 0;

  // Single upload - show simple card
  if (total === 1) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-80">
        <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
          <UploadItem upload={allUploads[0]} />
        </div>
      </div>
    );
  }

  // Multiple uploads - show summary with expandable list
  return (
    <div className="fixed bottom-4 right-4 z-50 w-80">
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        {/* Summary header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            {stats.active > 0 ? (
              <ArrowPathIcon className="h-5 w-5 text-blue-500 animate-spin" />
            ) : stats.failed > 0 ? (
              <XCircleIcon className="h-5 w-5 text-red-500" />
            ) : (
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
            )}
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">
                {stats.active > 0 
                  ? `Processing ${stats.active} of ${total} files`
                  : isAllDone && stats.failed === 0
                    ? `${total} files complete`
                    : `${stats.complete} complete, ${stats.failed} failed`
                }
              </p>
              {stats.active > 0 && (
                <p className="text-xs text-gray-500">
                  {stats.complete > 0 && `${stats.complete} done`}
                  {stats.complete > 0 && stats.failed > 0 && ', '}
                  {stats.failed > 0 && `${stats.failed} failed`}
                </p>
              )}
            </div>
          </div>
          {expanded ? (
            <ChevronDownIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronUpIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {/* Expandable file list */}
        {expanded && (
          <div className="border-t border-gray-200 max-h-64 overflow-y-auto">
            {allUploads.map(upload => (
              <UploadItem key={upload.id} upload={upload} compact />
            ))}
          </div>
        )}

        {/* Clear all button when done */}
        {isAllDone && (
          <div className="border-t border-gray-200 px-4 py-2">
            <button
              onClick={() => allUploads.forEach(u => clearUpload(u.id))}
              className="text-xs text-gray-500 hover:text-gray-700"
            >
              Dismiss all
            </button>
          </div>
        )}
      </div>
    </div>
  );
}